/**
 * W-8EXP Form Models
 * Following the same clean architecture pattern as W8BEN, W8ECI, and W9 models
 */

import { parseAddressComponents } from "../utils/formUtils";

// W8EXP Response Model - Fields that come from API endpoint (EXACT API FIELD NAMES)
export class W8EXPResponseModel {
  // Basic fields from API
  "UniqueIdentifier": string = "";
  "entityUniqueIdentifier": string = "";
  "Name of organization": string = "";
  "Country_of_incorporation_organization": string = "";
  "Form_name": string = "";
  "Form_revision": string = "";

  // Address fields from API
  "Permanent residence address": string = "";
  "Permanent address country": string = "";
  "EXP-Mailing_address_street": string = "";
  "EXP-Mailing_address_country": string = "";

  // Combined address fields from API (as shown in user examples)
  "EXP-Permanent_address_city_state_postal": string = "";
  "EXP-Mailing_address_city_state_postal": string = "";

  // Tax information from API
  "U.S. taxpayer identification number": string = "";
  "GIIN": string = "";
  "Foreign tax identifying number": string = "";
  "EXP-Reference_numbers": string = "";

  // Section 13 fields from API
  "EXP-13A_Text": string = "";
  "EXP-13B_Text": string = "";

  // ALL API Section Fields (matching your provided list exactly)
  "EXP-10A_Foreign_government": string = "";
  "EXP-10B_Integral_part": string = "";
  "EXP-10B_Integral_part_government_name": string = "";
  "EXP-10C_Controlled_entity": string = "";
  "EXP-10C_Controlled_entity_government_name": string = "";
  "EXP-10D_Not_commercial_activities": string = "";
  "EXP-10E_Central_bank_not_us_commercial": string = "";
  "EXP-11_International_organization": string = "";
  "EXP-12_Foreign_central_bank": string = "";
  "EXP-13A_Irs_determination": string = "";
  "EXP-13B_Us_counsel_opinion": string = "";
  "EXP-13C_Not_private_foundation": string = "";
  "EXP-13D_Is_private_foundation": string = "";
  "EXP-14_Us_territory_government": string = "";
  "EXP-15A_Qualified_holder": string = "";
  "EXP-15B_Foreign_partnership_qualified_holders": string = "";
  "EXP-16_Nonreporting_IGA_FFI": string = "";
  "EXP-16_nonreporting_iga_ffi_treatment": string = "";
  "EXP-16_nonreporting_iga_ffi_country": string = "";
  "EXP-16_nonreporting_iga_ffi_giin": string = "";
  "EXP-17_Territory_financial_institution": string = "";
  "EXP-18_Foreign_government_fatca": string = "";
  "EXP-19A_Retirement_plan_employees": string = "";
  "EXP-19B_Entity_identitfier": string = "";
  "EXP-20_501c_Organization_fatca": string = "";
  "EXP-21A_Passive_nffe_certification": string = "";
  "EXP-21B_No_substantial_us_owners": string = "";
  "EXP-21C_Has_substantial_us_owners": string = "";
  "EXP-22_sponsoring_entity_name": string = "";
  "EXP-22_Sponsored_direct_reporting": string = "";
  "EXP-4_foreign_government_central_bank": string = "";
  "EXP-4_international_organization": string = "";
  "EXP-501c_organization": string = "";
  "EXP-Direct_reporting_NFFE": string = "";
  "EXP-Exempt_retirement_plan": string = "";
  "EXP-Foreign_central_bank_issue": string = "";
  "EXP-Foreign_government": string = "";
  "EXP-Foreign_private_foundation": string = "";
  "EXP-Foreign_tax-exempt_organization": string = "";
  "EXP-Government_of_us_territory": string = "";
  "EXP-International_organization": string = "";
  "EXP-Nonreporting_IGA_FFI": string = "";
  "EXP-Participating_FFI": string = "";
  "EXP-Passive_NFFE": string = "";
  "EXP-Registered_deemed_compliant_FFI": string = "";
  "EXP-Reporting_Model1_FFI": string = "";
  "EXP-Reporting_Model2_FFI": string = "";
  "EXP-Sponsored_direct_reporting_NFFE": string = "";
  "EXP-Territory_financial_institution": string = "";
  "EXP-Withholding_qualified_holder": string = "";

  // Signature fields from API
  "Print_name": string = "";
  "Signature": string = "";
  "Signature_date": string = "";
  "Signature_date_on_form": string = "";
  "Signature_unreadable": boolean = false;
  "Signing_capacity": string = "";

  // Dynamic fields support
  [key: string]: any;

  constructor(data?: any) {
    if (data) {
      Object.assign(this, data);
    }
  }
}

export class W8EXPFormModel {
  // Basic identification fields (4 fields)
  "UniqueIdentifier": string = "";
  "entityUniqueIdentifier": string = "";
  "Name of organization": string = "";
  "Country_of_incorporation_organization": string = "";
  "Other": string = "";

  // Permanent Address fields (12 fields) - EXACT form field names
  "Permanent residence address": string = "";
  "Permanent address line 2": string = "";
  "Permanent address line 3": string = "";
  "Permanent address city": string = "";
  "Permanent address state USCA": string = "";
  "Permanent address state NonUS": string = "";
  "Permanent address zip": string = "";
  "Permanent address country": string = "";
  "Permanent address other": string = "";
  "Permanent address is care of": string = "no";
  "Permanent address is po box": string = "no";
  "Permanent address is rural route": string = "no";

  // Mailing Address fields (12 fields) - EXACT form field names
  "EXP-Mailing_address_street": string = "";
  "Mailing address line 2": string = "";
  "Mailing address line 3": string = "";
  "Mailing city address": string = ""; // Note: This is "city address", not "address city"
  "Mailing address state USCA": string = "";
  "Mailing address state NonUS": string = "";
  "Mailing address zip": string = "";
  "EXP-Mailing_address_country": string = "";
  "Mailing address other": string = "";
  "Mailing address is care of": string = "no";
  "Mailing address is po box": string = "no";
  "Mailing address is rural route": string = "no";

  // Taxpayer fields (6 fields) - EXACT form field names
  "Taxpayer type": string = "";
  "U.S. taxpayer identification number": string = "";
  "GIIN": string = "";
  "GIINCheckBox": boolean = false;
  "GIIN Applied For": boolean = false;
  "Foreign TIN": boolean = false;
  "Foreign tax identifying number": string = "";
  "EXP-Reference_numbers": string = "";

  // Chapter 3 Status Checkboxes (7 fields) - EXACT form field names
  "EXP-Foreign_central_bank_issue": boolean = false;
  "EXP-Foreign_government": boolean = false;
  "EXP-Foreign_private_foundation": boolean = false;
  "EXP-Foreign_tax-exempt_organization": boolean = false;
  "EXP-Government_of_us_territory": boolean = false;
  "EXP-International_organization": boolean = false;
  "EXP-Withholding_qualified_holder": boolean = false;

  // Chapter 4 FATCA Status Checkboxes (13 fields) - EXACT form field names
  "EXP-501c_organization": boolean = false;
  "EXP-Direct_reporting_NFFE": boolean = false;
  "EXP-Exempt_retirement_plan": boolean = false;
  "EXP-4_foreign_government_central_bank": boolean = false;
  "EXP-4_international_organization": boolean = false;
  "EXP-Nonreporting_IGA_FFI": boolean = false;
  "EXP-Participating_FFI": boolean = false;
  "EXP-Passive_NFFE": boolean = false;
  "EXP-Registered_deemed_compliant_FFI": boolean = false;
  "EXP-Reporting_Model1_FFI": boolean = false;
  "EXP-Reporting_Model2_FFI": boolean = false;
  "EXP-Sponsored_direct_reporting_NFFE": boolean = false;
  "EXP-Territory_financial_institution": boolean = false;

  // Sections 10-22 Fields (35 fields) - EXACT form field names
  "EXP-10A_Foreign_government": boolean = false;
  "EXP-10B_Integral_part": boolean = false;
  "EXP-10B_Integral_part_government_name": string = "";
  "EXP-10C_Controlled_entity": boolean = false;
  "EXP-10C_Controlled_entity_government_name": string = "";
  "EXP-10D_Not_commercial_activities": boolean = false;
  "EXP-10E_Central_bank_not_us_commercial": boolean = false;
  "EXP-11_International_organization": boolean = false;
  "EXP-12_Foreign_central_bank": boolean = false;
  "EXP-13A_Irs_determination": boolean = false;
  "EXP-13A_Text": string = "";
  "EXP-13A_tax_exempt_determination_letter_date": string = "";
  "EXP-13B_Us_counsel_opinion": boolean = false;
  "EXP-13B_Text": string = "";
  "EXP-13B_Date": string = "";
  "EXP-13C_Not_private_foundation": boolean = false;
  "EXP-13D_Is_private_foundation": boolean = false;
  "EXP-14_Us_territory_government": boolean = false;
  "EXP-15A_Qualified_holder": boolean = false;
  "EXP-15B_Foreign_partnership_qualified_holders": boolean = false;
  "EXP-16_Nonreporting_IGA_FFI": boolean = false;
  "EXP-16_nonreporting_iga_ffi_treatment": string = "";
  "EXP-16_nonreporting_iga_ffi_country": string = "";
  "EXP-16_nonreporting_iga_ffi_giin": string = "";
  "EXP-17_Territory_financial_institution": boolean = false;
  "EXP-18_Foreign_government_fatca": boolean = false;
  "EXP-19A_Retirement_plan_employees": boolean = false;
  "EXP-19B_Entity_identitfier": boolean = false;
  "EXP-20_501c_Organization_fatca": boolean = false;
  "EXP-21A_Passive_nffe_certification": boolean = false;
  "EXP-21B_No_substantial_us_owners": boolean = false;
  "EXP-21C_Has_substantial_us_owners": boolean = false;
  "EXP-22_Sponsored_direct_reporting": boolean = false;
  "EXP-22_sponsoring_entity_name": string = "";

  // Signature/Certification fields (4 fields) - EXACT form field names
  "Print_name": string = "";
  "Signing_capacity": boolean = false;
  "Signature": string = "";
  "isSigned": string = "no";
  "isModified": string = "no";
  "Signature_date": string = "";
  "Signature_date_on_form": string = "";
  "Signature_unreadable": boolean = false;

  // Mapping control field - prevents re-mapping when already done
  isMapDone: boolean = false;

  // Dynamic fields support
  [key: string]: any;

  constructor(data?: any) {
    if (data) {
      Object.assign(this, data);
    }
  }

  /**
   * Validate the form data
   */
  validate(): ValidationResult {
    const errors: string[] = [];
    const fieldErrors: Record<string, string> = {};

    // Updated required field validations per user request:
    // UniqueIdentifier, Name of organization - Permanent address, Date of birth, and Country of citizenship removed as mandatory
    const requiredFields = ["UniqueIdentifier", "Name of organization"];

    requiredFields.forEach((field) => {
      const value = (this as any)[field];
      if (!value || (typeof value === "string" && value.trim() === "")) {
        const fieldName = field.replace("EXP-", "").replace(/_/g, " ");
        const error = `${fieldName} is required`;
        errors.push(error);
        fieldErrors[field] = error;
      }
    });

    // Date validations (keep these as they may be needed)
    if (this["Date of birth"] && !this.isValidDate(this["Date of birth"])) {
      const error = "Date of birth must be a valid date";
      errors.push(error);
      fieldErrors["Date of birth"] = error;
    }

    return {
      isValid: errors.length === 0,
      errors,
      fieldErrors,
    };
  }

  /**
   * Check if a date string is valid
   */
  private isValidDate(dateString: string): boolean {
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime());
  }
}

export class ValidationResult {
  isValid: boolean = false;
  errors: string[] = [];
  fieldErrors: Record<string, string> = {};
}

/**
 * W8EXP Field Mapper - Maps response model fields to form model fields
 */
export class W8EXPFieldMapper {
  /**
   * Map response model to form model
   */
  static mapResponseToForm(responseModel: W8EXPResponseModel): W8EXPFormModel {
    const formModel = new W8EXPFormModel();

    // Check if mapping is already done to prevent re-mapping
    if (formModel.isMapDone) {
      console.log("W8EXP mapping already done, skipping...");
      return formModel;
    }

    // Direct field mappings (fields that exist in BOTH API response and form models)
    const directMappings = [
      // Basic fields that exist in both API and form
      "UniqueIdentifier",
      "entityUniqueIdentifier",
      "Name of organization",
      "Country_of_incorporation_organization",
      "U.S. taxpayer identification number",
      "GIIN",
      "EXP-Mailing_address_country",
      "Foreign tax identifying number",
      "EXP-Reference_numbers",
      "Print_name",
      "Signature_date",
      "Signature_unreadable",

      // Section fields that exist in both API and form
      "EXP-10A_Foreign_government",
      "EXP-10B_Integral_part",
      "EXP-10B_Integral_part_government_name",
      "EXP-10C_Controlled_entity",
      "EXP-10C_Controlled_entity_government_name",
      "EXP-10D_Not_commercial_activities",
      "EXP-10E_Central_bank_not_us_commercial",
      "EXP-11_International_organization",
      "EXP-12_Foreign_central_bank",
      "EXP-13A_Irs_determination",
      "EXP-13A_tax_exempt_determination_letter_date",
      "EXP-13A_Text",
      "EXP-13B_Us_counsel_opinion",
      "EXP-13B_Text",
      "EXP-13C_Not_private_foundation",
      "EXP-13D_Is_private_foundation",
      "EXP-14_Us_territory_government",
      "EXP-15A_Qualified_holder",
      "EXP-15B_Foreign_partnership_qualified_holders",
      "EXP-16_Nonreporting_IGA_FFI",
      "EXP-16_nonreporting_iga_ffi_treatment",
      "EXP-16_nonreporting_iga_ffi_country",
      "EXP-16_nonreporting_iga_ffi_giin",
      "EXP-17_Territory_financial_institution",
      "EXP-18_Foreign_government_fatca",
      "EXP-19A_Retirement_plan_employees",
      "EXP-19B_Entity_identitfier",
      "EXP-20_501c_Organization_fatca",
      "EXP-21A_Passive_nffe_certification",
      "EXP-21B_No_substantial_us_owners",
      "EXP-21C_Has_substantial_us_owners",
      "EXP-22_sponsoring_entity_name",
      "EXP-22_Sponsored_direct_reporting",
      "EXP-4_foreign_government_central_bank",
      "EXP-4_international_organization",
      "EXP-501c_organization",
      "EXP-Direct_reporting_NFFE",
      "EXP-Exempt_retirement_plan",
      "EXP-Foreign_central_bank_issue",
      "EXP-Foreign_government",
      "EXP-Foreign_private_foundation",
      "EXP-Foreign_tax-exempt_organization",
      "EXP-Government_of_us_territory",
      "EXP-International_organization",
      "EXP-Nonreporting_IGA_FFI",
      "EXP-Participating_FFI",
      "EXP-Passive_NFFE",
      "EXP-Registered_deemed_compliant_FFI",
      "EXP-Reporting_Model1_FFI",
      "EXP-Reporting_Model2_FFI",
      "EXP-Sponsored_direct_reporting_NFFE",
      "EXP-Territory_financial_institution",
      "EXP-Withholding_qualified_holder",
    ];

    directMappings.forEach((field) => {
      if (responseModel[field] !== undefined) {
        formModel[field] = responseModel[field];
      }
    });

    // Special mappings
    this.mapSpecialFields(responseModel, formModel);

    // Handle dynamic fields from response
    Object.keys(responseModel).forEach((key) => {
      if (!directMappings.includes(key) && responseModel[key] !== undefined) {
        formModel[key] = responseModel[key];
      }
    });

    // Apply boolean conversion AFTER all mappings to ensure checkbox fields work properly
    this.convertBooleanFields(formModel);

    // Auto-set isSigned and Signature_unreadable based on Signature field (only during initial mapping)
    if (formModel.Signature && formModel.Signature.trim() !== "") {
      formModel.isSigned = "yes";
      formModel.Signature_unreadable = true;
    } else if (formModel.isSigned !== "yes" && formModel.isSigned !== "no") {
      formModel.isSigned = "no";
    }

    // Set mapping done flag to prevent re-mapping
    // formModel.isMapDone = true;
    console.log("W8EXP field mapping completed");

    return formModel;
  }

  /**
   * Convert boolean fields that may come as strings from API to actual booleans
   */
  private static convertBooleanFields(formModel: W8EXPFormModel): void {
    const booleanFields = [
      "EXP-Foreign_central_bank_issue",
      "EXP-Foreign_government",
      "EXP-Foreign_private_foundation",
      "EXP-Foreign_tax-exempt_organization",
      "EXP-Government_of_us_territory",
      "EXP-International_organization",
      "EXP-Withholding_qualified_holder",
      "EXP-501c_organization",
      "EXP-Direct_reporting_NFFE",
      "EXP-Exempt_retirement_plan",
      "EXP-4_foreign_government_central_bank",
      "EXP-4_international_organization",
      "EXP-Nonreporting_IGA_FFI",
      "EXP-Participating_FFI",
      "EXP-Passive_NFFE",
      "EXP-Registered_deemed_compliant_FFI",
      "EXP-Reporting_Model1_FFI",
      "EXP-Reporting_Model2_FFI",
      "EXP-Sponsored_direct_reporting_NFFE",
      "EXP-Territory_financial_institution",
      "EXP-10A_Foreign_government",
      "EXP-10B_Integral_part",
      "EXP-10C_Controlled_entity",
      "EXP-10D_Not_commercial_activities",
      "EXP-10E_Central_bank_not_us_commercial",
      "EXP-11_International_organization",
      "EXP-12_Foreign_central_bank",
      "EXP-13A_Irs_determination",
      "EXP-13B_Us_counsel_opinion",
      "EXP-13C_Not_private_foundation",
      "EXP-13D_Is_private_foundation",
      "EXP-14_Us_territory_government",
      "EXP-15A_Qualified_holder",
      "EXP-15B_Foreign_partnership_qualified_holders",
      "EXP-16_Nonreporting_IGA_FFI",
      "EXP-17_Territory_financial_institution",
      "EXP-18_Foreign_government_fatca",
      "EXP-19A_Retirement_plan_employees",
      "EXP-19B_Entity_identitfier",
      "EXP-20_501c_Organization_fatca",
      "EXP-21A_Passive_nffe_certification",
      "EXP-21B_No_substantial_us_owners",
      "EXP-21C_Has_substantial_us_owners",
      "EXP-22_Sponsored_direct_reporting",
      "GIINCheckBox",
      "GIIN Applied For",
      "Foreign TIN",
    ];

    booleanFields.forEach((field) => {
      if (formModel[field] !== undefined) {
        const value = formModel[field];
        if (typeof value === "string") {
          formModel[field] =
            value.toLowerCase() === "true" ||
            value === "1" ||
            value.toLowerCase() === "yes" ||
            value.toLowerCase() === "selected";
        } else if (typeof value === "number") {
          formModel[field] = value === 1;
        } else {
          formModel[field] = !!value;
        }
      }
    });
  }

  /**
   * Handle special field mappings and transformations
   */
  private static mapSpecialFields(
    responseModel: W8EXPResponseModel,
    formModel: W8EXPFormModel
  ): void {
    // Helper function to check if a value is truthy (handles both string and boolean)
    const isTruthyValue = (value: any): boolean => {
      return value === "selected" || value === "true" || value === true || value === 1;
    };

    // Map address fields from API to form - API has fewer address fields
    if (responseModel["Permanent residence address"]) {
      formModel["Permanent residence address"] = responseModel["Permanent residence address"];
    }

    // Handle combined permanent address field
    if (responseModel["EXP-Permanent_address_city_state_postal"]) {
      const addressComponents = parseAddressComponents(
        responseModel["EXP-Permanent_address_city_state_postal"]
      );
      formModel["Permanent address city"] = addressComponents.city;
      formModel["Permanent address state NonUS"] = addressComponents.state;
      formModel["Permanent address zip"] = addressComponents.postalCode;
    }

    if (responseModel["Permanent address country"]) {
      formModel["Permanent address country"] = responseModel["Permanent address country"];
    }

    // Handle combined mailing address field
    if (responseModel["EXP-Mailing_address_city_state_postal"]) {
      const addressComponents = parseAddressComponents(
        responseModel["EXP-Mailing_address_city_state_postal"]
      );
      formModel["Mailing city address"] = addressComponents.city;
      formModel["Mailing address state NonUS"] = addressComponents.state;
      formModel["Mailing address zip"] = addressComponents.postalCode;
    }

    // Skip boolean conversion - handled by W8EXPService.handleCheckboxDefaults()

    // Map signature fields
    if (responseModel["Signature"]) {
      formModel["Signature"] = responseModel["Signature"];
    }

    // Convert string boolean values to actual booleans for checkboxes
    const booleanFields = [
      "EXP-Foreign_government",
      "EXP-International_organization",
      "EXP-Foreign_central_bank_issue",
      "EXP-Foreign_tax-exempt_organization",
      "EXP-Foreign_private_foundation",
      "EXP-Government_of_us_territory",
      "EXP-Withholding_qualified_holder",
      "EXP-Participating_FFI",
      "EXP-Reporting_Model1_FFI",
      "EXP-Reporting_Model2_FFI",
      "EXP-Registered_deemed_compliant_FFI",
      "EXP-Nonreporting_IGA_FFI",
      "EXP-Territory_financial_institution",
      "EXP-4_international_organization",
      "EXP-4_foreign_government_central_bank",
      "EXP-Exempt_retirement_plan",
      "EXP-501c_organization",
      "EXP-Passive_NFFE",
      "EXP-Direct_reporting_NFFE",
      "EXP-Sponsored_direct_reporting_NFFE",
      "Signing_capacity",
      "GIINCheckBox",
      "GIIN Applied For",
      "Foreign TIN",
    ];

    booleanFields.forEach((field) => {
      const value = responseModel[field];
      formModel[field] = isTruthyValue(value);
    });

    // Map additional response fields to form fields
    Object.keys(responseModel).forEach((responseField) => {
      const value = responseModel[responseField];
      if (value !== undefined && value !== null && value !== "") {
        // Direct assignment for fields that exist in form model
        if ((formModel as any)[responseField] !== undefined) {
          (formModel as any)[responseField] = value;
        }

        // Handle API fields with underscores/dashes - map to form fields with spaces
        const formFieldName = responseField.replace(/_/g, " ").replace(/-/g, " ");
        if (formFieldName !== responseField && (formModel as any)[formFieldName] !== undefined) {
          console.log(
            `W8EXP mapping API field "${responseField}" to form field "${formFieldName}"`
          );
          (formModel as any)[formFieldName] = value;
        }
      }
    });
  }
}
